import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';

import { useDebounce } from '@/shared/hooks/useDebounce';
import { useTableState } from '@/shared/hooks/useTableState';
import type { SelectMenuOption } from '@/shared/ui';

import { EMPTY_ORDER_STATS } from '../lib/orderManagementModel';
import {
  fetchOrderList,
  orderSummaryToStats,
  useOrderSummaryQuery,
  type OrderListQueryResult,
} from '../api/ordersApi';
import { ordersKeys } from '../api/keys';
import type {
  OrderListSortBy,
  OrderListSortOrder,
  OrderStatus,
  OrdersListParams,
} from '../types';
import { ORDER_LIST_FILTER_STATUSES } from '../types';
import { useWarehousesOptions } from './useWarehousesOptions';

const SEARCH_DEBOUNCE_MS = 300;

export function useOrderManagementState() {
  const { t } = useTranslation('orders');
  const [query, setQuery] = useState('');
  const [originId, setOriginId] = useState('all');
  const [statusFilter, setStatusFilter] = useState<'all' | OrderStatus>('all');
  const [sortBy, setSortBy] = useState<OrderListSortBy>('recent');
  const [sortOrder, setSortOrder] = useState<OrderListSortOrder | undefined>(undefined);

  const debouncedOrderNumber = useDebounce(query, SEARCH_DEBOUNCE_MS);

  const table = useTableState({
    tableId: 'orders',
    defaultPageSize: 20,
    defaultSorting: [],
  });

  const warehousesQuery = useWarehousesOptions();

  const storeIdForApi = originId === 'all' ? undefined : originId;
  const orderNumberForApi = debouncedOrderNumber.trim() || undefined;
  const statusForApi = statusFilter === 'all' ? undefined : statusFilter;

  const listParams: OrdersListParams = useMemo(
    () => ({
      page: table.pageIndex + 1,
      limit: table.pageSize,
      storeId: storeIdForApi,
      status: statusForApi,
      orderNumber: orderNumberForApi,
      sortBy,
      ...(sortOrder ? { sortOrder } : {}),
    }),
    [
      table.pageIndex,
      table.pageSize,
      storeIdForApi,
      statusForApi,
      orderNumberForApi,
      sortBy,
      sortOrder,
    ],
  );

  const listQuery = useQuery<OrderListQueryResult>({
    queryKey: ordersKeys.list(listParams as Record<string, unknown>),
    queryFn: () => fetchOrderList(listParams),
    staleTime: 15_000,
    retry: 1,
  });

  const summaryQuery = useOrderSummaryQuery({ storeId: storeIdForApi }, { enabled: true });

  const stats = useMemo(() => {
    if (!summaryQuery.data) return EMPTY_ORDER_STATS;
    return orderSummaryToStats(summaryQuery.data);
  }, [summaryQuery.data]);

  const rows = listQuery.data?.items ?? [];

  const originOptions = useMemo<SelectMenuOption[]>(() => {
    if (!warehousesQuery.data) {
      return [];
    }
    return warehousesQuery.data;
  }, [warehousesQuery.data]);

  const statusOptions = useMemo<SelectMenuOption[]>(
    () => [
      { value: 'all', label: t('filters.statusAll') },
      ...ORDER_LIST_FILTER_STATUSES.map((status) => ({
        value: status,
        label: t(`status.${status}` as 'status.CONFIRMED'),
      })),
    ],
    [t],
  );

  const sortOptions = useMemo(
    () => [
      { value: 'recent' as const, label: t('filters.sortRecent') },
      { value: 'orderNumber' as const, label: t('filters.sortOrderNumber') },
    ],
    [t],
  );

  const onQueryChange = useCallback(
    (nextQuery: string) => {
      setQuery(nextQuery);
      table.resetPagination();
    },
    [table],
  );

  const onOriginChange = useCallback(
    (nextOriginId: string) => {
      setOriginId(nextOriginId || 'all');
      table.resetPagination();
    },
    [table],
  );

  const onStatusFilterChange = useCallback(
    (nextStatus: string) => {
      setStatusFilter((nextStatus || 'all') as 'all' | OrderStatus);
      table.resetPagination();
    },
    [table],
  );

  const onSortByChange = useCallback(
    (nextSort: OrderListSortBy) => {
      setSortBy(nextSort);
      // Let API defaults apply (recent→desc, orderNumber→asc) unless user set direction later
      setSortOrder(undefined);
      table.resetPagination();
    },
    [table],
  );

  return {
    t,
    stats,
    statsLoading: summaryQuery.isLoading,
    statsError: summaryQuery.isError,
    statsErrorMessage:
      summaryQuery.error instanceof Error ? summaryQuery.error.message : undefined,
    refetchStats: summaryQuery.refetch,
    listLoading: listQuery.isLoading,
    listFetching: listQuery.isFetching,
    listError: listQuery.isError,
    listErrorMessage: listQuery.error instanceof Error ? listQuery.error.message : undefined,
    refetchList: listQuery.refetch,
    query,
    onQueryChange,
    originId,
    originOptions,
    onOriginChange,
    statusFilter,
    statusOptions,
    onStatusFilterChange,
    sortBy,
    sortOptions,
    onSortByChange,
    rows,
    paged: rows,
    totalRows: listQuery.data?.total ?? 0,
    pageCount: listQuery.data?.pageCount ?? 1,
    sorting: table.sorting,
    setSorting: table.setSorting,
    pageIndex: table.pageIndex,
    pageSize: table.pageSize,
    setPageIndex: table.setPageIndex,
    setPageSize: table.setPageSize,
  };
}
